/**
 * Simple i18n utility functions
 * Provides easy access to translations in JavaScript
 */

/**
 * Get the global translation service instance
 * @returns {TranslationService} Translation service instance
 */
function getTranslationService() {
  if (window.TranslationService) {
    return window.TranslationService;
  }
  
  // Fallback if service not loaded
  console.warn('TranslationService not found, creating minimal fallback');
  return {
    t: (key, options = {}) => `[${key}]`,
    exists: () => false,
    scope: () => ({}),
    getLocale: () => 'fr'
  };
}

/**
 * Translate a key
 * @param {string} key - Translation key
 * @param {Object} options - Translation options
 * @returns {string} Translated text
 */
export function t(key, options = {}) {
  return getTranslationService().t(key, options);
}

/**
 * Check if translation exists
 * @param {string} key - Translation key
 * @returns {boolean}
 */
export function exists(key) {
  return getTranslationService().exists(key);
}

/**
 * Get translations for a scope
 * @param {string} scope - Translation scope
 * @returns {Object} Scoped translations
 */
export function scope(scope) {
  return getTranslationService().scope(scope);
}

/**
 * Get current locale
 * @returns {string} Current locale
 */
export function locale() {
  return getTranslationService().getLocale();
}

/**
 * School certificates specific translations
 * Provides easy access to school certificates translations
 */
export const schoolCertificates = {
  /**
   * Get error message
   * @param {string} key - Error key
   * @param {Object} options - Options
   * @returns {string} Error message
   */
  error(key, options = {}) {
    return t(`errors.${key}`, options);
  },

  /**
   * Get confirmation message
   * @param {string} key - Confirmation key
   * @param {Object} options - Options
   * @returns {string} Confirmation message
   */
  confirm(key, options = {}) {
    return t(`confirmations.${key}`, options);
  },

  /**
   * Get progress message
   * @param {string} key - Progress key
   * @param {Object} options - Options
   * @returns {string} Progress message
   */
  progress(key, options = {}) {
    return t(`progress.${key}`, options);
  },

  /**
   * Get notification message
   * @param {string} key - Notification key
   * @param {Object} options - Options
   * @returns {string} Notification message
   */
  notification(key, options = {}) {
    return t(`notifications.${key}`, options);
  },

  /**
   * Get counter text
   * @param {string} key - Counter key
   * @param {number} count - Count for pluralization
   * @param {Object} options - Additional options
   * @returns {string} Counter text
   */
  counter(key, count, options = {}) {
    return t(`counters.${key}`, { count, ...options });
  },

  /**
   * Get placeholder text
   * @param {string} key - Placeholder key
   * @param {Object} options - Options
   * @returns {string} Placeholder text
   */
  placeholder(key, options = {}) {
    return t(`placeholders.${key}`, options);
  },

  /**
   * Get log message
   * @param {string} key - Log key
   * @param {Object} options - Options
   * @returns {string} Log message
   */
  log(key, options = {}) {
    return t(`log.messages.${key}`, options);
  }
};

// Make functions available globally for backward compatibility
if (typeof window !== 'undefined') {
  window.i18n = {
    t,
    exists,
    scope,
    locale,
    schoolCertificates
  };
}

export default {
  t,
  exists,
  scope,
  locale,
  schoolCertificates
};